// Spec: specs/stats/questions/functional_requirements.md §6.10

import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import PreloaderEngine from '@aytm/app/stats/preloader/engine'
import { FORMS } from '@aytm/app/stats/preloader/forms'
import { reportPalette } from '@aytm/app/stats/preloader/index'

import style from '@aytm/app/stats/page/question-views/components/data-run/styles/Field.module.scss'

// A field held still still runs its clock: it is held by population, never by withholding the step,
// because the step is also what carries a palette change across.
const HELD = 1e6

// The field spans the block it stands in. Height follows the width so a wide card does not get a
// thin strip, and the bead count follows the area so it does not get a sparse one.
// The field's model is about 2.2 wide to 1 tall, and the engine fits it to whichever axis binds
// first. Give it less height than that and the drawing shrinks to the middle of a wide canvas
// rather than spanning it.
const RATIO = 1 / 2.2
const MIN_H = 160
const MAX_H = 320
const BEADS_PER_PX = 0.8
const MIN_BEADS = 320
const MAX_BEADS = 1100

// A field held on one form shows whichever it happened to start on, and the compact ones -- a cube,
// a ring -- sit in the middle of a wide card however tall it is. Hold it on the shape of a chart
// instead: it spans, and it is the chart this block has not got yet.
const HELD_FORM = Math.max(
  0,
  FORMS.findIndex(form => 'bars' === form.key)
)

const clamp = (value, low, high) => Math.max(low, Math.min(high, value))

const reduced = () => Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)

// The mark a state carries: the same field the page draws while the report loads, held at the
// population and the motion its cause calls for. It reports nothing and measures nothing.
const Field = ({ population = 1, still = false, colorTheme }) => {
  const boxRef = useRef(null)
  const canvasRef = useRef(null)
  const engineRef = useRef(null)

  useEffect(() => {
    const box = boxRef.current
    const canvas = canvasRef.current
    if (!box || !canvas || 'function' != typeof canvas.getContext) return

    // measured off the box, never off the canvas: the engine writes the canvas its own pixel size,
    // so measuring that would feed the last frame's width back in as this frame's
    const width = Math.max(240, box.clientWidth || 400)
    const height = Math.round(clamp(width * RATIO, MIN_H, MAX_H))
    const beads = Math.round(clamp(width * BEADS_PER_PX, MIN_BEADS, MAX_BEADS))

    let engine = null
    try {
      engine = new PreloaderEngine(canvas, {
        width,
        height,
        beads,
        size: 4.2,
        core: 18,
        population: Math.round(beads * population),
        palette: reportPalette(),
        seed: `state-${population}-${still ? 'held' : 'live'}`,
        ...(still && { hold: HELD, idleSpin: (1.6 * Math.PI) / 180, onlyForm: HELD_FORM })
      })
    } catch {
      return // a surface that cannot draw simply carries no mark
    }

    engineRef.current = engine
    engine.hintPending = false
    engine.hasDragged = true
    if (still) {
      engine.fromIdx = HELD_FORM
      engine.toIdx = HELD_FORM
    }
    engine.addBeads(engine.o.beads)

    engine.render(0) // one frame, so a field that never runs is still drawn

    // A report can hold dozens of these. Nothing animates while it is off screen or while the tab
    // is in the background: an unattended canvas costs the reader's machine and shows them nothing.
    let onScreen = false
    const settle = () => {
      const shouldRun = onScreen && !reduced() && 'hidden' !== document.visibilityState
      if (shouldRun) engine.start()
      else engine.stop()
    }

    const seen =
      'function' == typeof window.IntersectionObserver
        ? new window.IntersectionObserver(
            entries => {
              onScreen = entries.some(entry => entry.isIntersecting)
              settle()
            },
            { rootMargin: '120px' }
          )
        : null

    if (seen) seen.observe(box)
    else {
      onScreen = true // no observer to ask: draw rather than stay dark
      settle()
    }

    document.addEventListener('visibilitychange', settle)

    const observer =
      'function' == typeof window.ResizeObserver
        ? new window.ResizeObserver(() => {
            const next = Math.max(240, box.clientWidth || width)
            if (next === engine.o.width) return
            engine.o.width = next
            engine.o.height = Math.round(clamp(next * RATIO, MIN_H, MAX_H))
            engine._resize()
            // resizing clears the canvas; a paused field has no frame coming to redraw it
            if (!engine.raf) engine.render(0)
          })
        : null
    observer?.observe(box)

    return () => {
      document.removeEventListener('visibilitychange', settle)
      seen?.disconnect()
      observer?.disconnect()
      engine.stop()
      engineRef.current = null
    }
  }, [population, still])

  // The report's palette is the reader's choice, and it changes under a state that is already up.
  // Keyed on the colours themselves: the model hands back a fresh array every call, so the array
  // identity would re-fire this on every render and never on a change.
  const paletteKey = Array.isArray(colorTheme) ? colorTheme.join(',') : ''
  useEffect(() => {
    engineRef.current?.setPalette(reportPalette())
  }, [paletteKey])

  return (
    <div ref={boxRef} className={style.box}>
      <canvas ref={canvasRef} className={style.field} aria-hidden="true" />
    </div>
  )
}

Field.displayName = 'Field'

Field.propTypes = {
  population: PropTypes.number,
  still: PropTypes.bool,
  colorTheme: PropTypes.array
}

export default Field
